
import { ManagedDocumentResponse } from '@/model/Document';
import FileUsageType from '@/model/enums/FileUsageType';
import ToastMixin from '@/mixins/ToastMixin';
import ToastVariant from '@/model/enums/toast/toast-variant';
import { ApiError } from '@/model/ApiError';
import { mixins } from 'vue-class-component';
import { mapActions, mapGetters } from 'vuex';
import { Component } from 'vue-property-decorator';
import { EXCEL_FILE_ACCEPT, isExcelFile } from '@/lib/excel-util';

type DocumentDescriptor = {
    usageType: FileUsageType;
    label: string;
    uploadLabel: string;
    accept: string;
    fileType: 'pdf' | 'excel';
};

@Component({
    methods: {
        ...mapActions({
            fetchDocuments: 'adminStorage/fetchDocuments',
            uploadDocument: 'adminStorage/uploadDocument',
            downloadExcelTemplate: 'adminStorage/downloadExcelTemplate',
        }),
    },
    computed: {
        ...mapGetters({
            documents: 'adminStorage/getDocuments',
            documentsLoading: 'adminStorage/getDocumentsLoading',
            adminError: 'adminStorage/getError',
        }),
    },
})
export default class AdminDocuments extends mixins(ToastMixin) {
    documents!: ManagedDocumentResponse[];

    documentsLoading!: boolean;

    adminError!: ApiError | null;

    fetchDocuments!: () => Promise<void>;

    uploadDocument!: (request: {
        usageType: FileUsageType;
        file: File;
    }) => Promise<ManagedDocumentResponse>;

    downloadExcelTemplate!: () => Promise<void>;

    uploadingDocuments: Record<FileUsageType, boolean> = {
        [FileUsageType.GTC]: false,
        [FileUsageType.GDPR]: false,
        [FileUsageType.EXCEL_TEMPLATE]: false,
    };

    downloadingExcelTemplate = false;

    get documentDescriptors(): DocumentDescriptor[] {
        return [
            {
                usageType: FileUsageType.GTC,
                label: this.$t('admin.documents.items.gtc').toString(),
                uploadLabel: this.$t('admin.documents.upload-pdf').toString(),
                accept: 'application/pdf,.pdf',
                fileType: 'pdf',
            },
            {
                usageType: FileUsageType.GDPR,
                label: this.$t('admin.documents.items.gdpr').toString(),
                uploadLabel: this.$t('admin.documents.upload-pdf').toString(),
                accept: 'application/pdf,.pdf',
                fileType: 'pdf',
            },
            {
                usageType: FileUsageType.EXCEL_TEMPLATE,
                label: this.$t(
                    'admin.documents.items.excel-template',
                ).toString(),
                uploadLabel: this.$t('admin.documents.upload-excel').toString(),
                accept: EXCEL_FILE_ACCEPT,
                fileType: 'excel',
            },
        ];
    }

    beforeMount(): void {
        this.$store.commit('adminStorage/setError', null);
        this.fetchDocuments();
    }

    getDocumentFileName(usageType: FileUsageType): string | null {
        return (
            this.documents.find((document) => document.usageType === usageType)
                ?.fileName ?? null
        );
    }

    getSystemDocumentLink(usageType: FileUsageType): string {
        const baseUrl = (process.env.VUE_APP_BASE_URL || '').replace(
            /\/+$/,
            '',
        );

        return `${baseUrl}/documents/${usageType}/view`;
    }

    onFileChange(event: Event, descriptor: DocumentDescriptor): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        const { usageType } = descriptor;

        if (!file) {
            return;
        }

        if (!this.isValidFile(file, descriptor)) {
            const messageKey =
                descriptor.fileType === 'excel'
                    ? 'admin.documents.toast.invalid-excel.message'
                    : 'admin.documents.toast.invalid-file.message';

            this.showToast(
                this.$t(messageKey).toString(),
                this.$t('admin.documents.toast.invalid-file.title').toString(),
                ToastVariant.DANGER,
            );
            input.value = '';
            return;
        }

        this.uploadingDocuments[usageType] = true;
        this.uploadDocument({ usageType, file })
            .then(() => {
                this.showToast(
                    this.$t('admin.documents.toast.upload.success').toString(),
                    this.$t('admin.documents.toast.upload.title').toString(),
                    ToastVariant.SUCCESS,
                );
            })
            .catch(() => {
                if (this.adminError) {
                    this.showToast(
                        this.$t(this.adminError.key).toString(),
                        this.$t('customer-project.error-title').toString(),
                        ToastVariant.DANGER,
                    );
                }
            })
            .finally(() => {
                this.uploadingDocuments[usageType] = false;
                input.value = '';
            });
    }

    onDownloadExcelTemplate(): void {
        this.downloadingExcelTemplate = true;
        this.downloadExcelTemplate()
            .catch(() => {
                this.showToast(
                    this.$t(
                        'admin.documents.toast.download.message',
                    ).toString(),
                    this.$t('admin.documents.toast.download.title').toString(),
                    ToastVariant.DANGER,
                );
            })
            .finally(() => {
                this.downloadingExcelTemplate = false;
            });
    }

    isExcelTemplate(usageType: FileUsageType): boolean {
        return usageType === FileUsageType.EXCEL_TEMPLATE;
    }

    hasExcelTemplate(usageType: FileUsageType): boolean {
        return (
            this.isExcelTemplate(usageType) &&
            Boolean(this.getDocumentFileName(usageType))
        );
    }

    isValidFile(file: File, descriptor: DocumentDescriptor): boolean {
        return descriptor.fileType === 'pdf'
            ? this.isPdf(file)
            : this.isExcel(file);
    }

    isExcel(file: File): boolean {
        return isExcelFile(file);
    }

    isPdf(file: File): boolean {
        const lowerCaseName = file.name.toLowerCase();

        return (
            file.type === 'application/pdf' || lowerCaseName.endsWith('.pdf')
        );
    }
}
